// Pure decision logic for the IME-blind "stable window size" (see
// use-stable-window-size.ts). DOM-free so vitest (node env) can exercise
// every branch.
//
// Since the keyboard-resize fix (interactive-widget=resizes-content on web,
// the native WebView resizer in the APK), the layout viewport shrinks while
// the on-screen keyboard is open. Adaptive layout breakpoints must NOT react
// to that shrink — otherwise they can unmount the very input that summoned
// the keyboard (focus loss closes the keyboard and the UI loops). This
// reducer keeps the previous size while a text-editable element is focused
// and the height shrinks, which is the keyboard-open signature.
//
// Known, intentional trade-off (do NOT "improve" the width rule): rotating
// with the keyboard open is indistinguishable from a genuine resize, so the
// width-change rule temporarily bakes the keyboard-shrunk height of the new
// orientation into the stable size. The compaction focus guard (see
// use-cart-compaction.ts) is what prevents any fatal unmount during that
// window, and the growth rule (keyboard closing) or the post-blur resync in
// the composable heals it moments later.

export interface IWindowSize {
  width: number;
  height: number;
}

/**
 * One step of the stable-size reduction, run on every raw window resize.
 *
 * - No text-editable element focused: no keyboard can be open — accept.
 * - Width changed: rotation / desktop resize, never the keyboard — accept.
 * - Height grew: keyboard closing (or genuine growth) — accept.
 * - Height shrank while a text field is focused: presumed keyboard — keep
 *   the previous size.
 */
export function resolveStableSize(
  prev: IWindowSize,
  next: IWindowSize,
  textEditableFocused: boolean
): IWindowSize {
  if (!textEditableFocused) return next;
  if (next.width !== prev.width) return next;
  if (next.height > prev.height) return next;
  return prev;
}
